import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Checkbox, Dropdown, Field, Groupper, Header, Input, Message, Modal, Table, Toolbar, useFrontier } from "react-frontier2";
import { Location, SetLoading, Venue } from "@arema/components/Classes";
import { PDV, PaymentTerminal } from '../../AdminClasses';
import { bindClick, bindFormChange, checkLocation } from "@arema/components/Util";
import { DeletedBanner, LocationView, NotFound, PlacesSearch, SearchTerminalModal, SearchVenueSectionModal } from "../../components";
import { QRCodeSVG } from "qrcode.react";
import { useUser } from "../../AdminHooks";
import { useTranslation } from "react-i18next";
import API, { PDVEditData } from "../../API";
import Validator from "@arema/components/Validator";
import UserAccess from "../../UserAccess";
import { useTitle } from "@arema/components/Hooks";

interface Props {
	pdv_id?: string,
	pdv?: PDV,
	onEditPdv?: (pdv: PDV)=>void
}

enum Modals {
	REGISTER_TOKEN = 1,
	RESET_TOKEN = 2,

	VENUE_MODAL = 10,
	TERMINAL_MODAL = 11,
}

const PDV_TYPES = [
	{ pdv_type: 1, type_name: 'Taquilla' },
	{ pdv_type: 150, type_name: 'Interno AREMA' },
	{ pdv_type: 151, type_name: 'PDV Pruebas' },
]

var OptionText = (props: { title: string, text: string })=>{
	return <div>
		<div style={{ fontSize: 14, fontWeight: 'bold', lineHeight: '14px' }}>{props.title}</div>
		<div style={{ fontSize: 13, color: 'gray', lineHeight: '13px', marginTop: 2 }}>{props.text}</div>
	</div>
}

const PDVCreate = (props: Props)=>{
	const navigate = useNavigate()
	let pdv_id = props.pdv_id;
	var IS_CREATE = !props.pdv;
	var { t } = useTranslation();
	var { setTitle } = useTitle();
	var { user, hasAccess } = useUser();
	var { Toast } = useFrontier();
	var [pdv, setPdv] = useState<PDV>(null);
	var [editLocation, setEditLocation] = useState<boolean>(false);
	var [newLocation, setNewLocation] = useState<Location>(null);
	var [errorPrompts, setErrorPrompts] = useState<string[]>(null);
	var [shownModal, setShownModal] = useState<Modals>(null);
	var [modalConfirm, setModalConfirm] = useState<boolean>(false);
	var [modalPrompts, setModalPrompts] = useState<string[]>(null);

	useEffect(()=>{
		if(IS_CREATE) setTitle('Crear PDV');
		else if(props.pdv) setTitle(`PDV: ${props.pdv.pdv_name}`);
		setPdv(props.pdv || {
			pdv_name: '',
			location: null,
			token: null,
			venue_id: null,
			external_terminal: false,
			venue_name: '',
			pdv_type: null,
			ignore_limits: false,
			exclude_commission: false,
			report_access: false,
			money_supervised: false,
		});
	}, [props.pdv]);

	const submit = async (setLoading: SetLoading)=>{
		var body: any = {
			pdv_id: null,
			pdv_name: pdv.pdv_name,
			location: pdv.location,
			venue_id: pdv.venue_id,
			pdv_type: pdv.pdv_type,
		}
		if(IS_CREATE || newLocation){
			body.location = newLocation;
		}
		var { valid, prompts } = Validator(body, {
			pdv_name: [{
				rule: 'length', params: [4, 64], label: 'Nombre'
			}],
			pdv_type : [{ rule: 'number' }],
			...((IS_CREATE || newLocation) ? {
				location: [{ rule: checkLocation, label: 'Ubicación', prompt: 'Ubicacion no valida' }]
			} : {})
		});
		setErrorPrompts(prompts);
		if(!valid) return;

		if(!IS_CREATE){
			body.pdv_id = parseInt(pdv_id);
		}

		setLoading(true);
		if(IS_CREATE){
			API.createPDV({
				pdv_name: body.pdv_name,
				pdv_type: body.pdv_type,
				venue_id: body.venue_id,
				all_events: body.all_events,
				location: body.location,
				terminal_id: pdv.terminal_id,
				external_terminal: !!pdv.external_terminal,
				// report_access: !!pdv.report_access,
				// exclude_commission: !!pdv.exclude_commission
			}).then(res=>{
				if(res.error) return setErrorPrompts([res.message]);
				Toast.success('Se ha creado el punto de venta');
				navigate(`/pdv/${res.data.pdv_id}`);
			}).catch(err=>{
				setErrorPrompts(['Hubo un error inesperado creando el Punto de Venta (LCL-10)']);
			}).finally(()=>{
				setLoading(false);
			});
		}else{
			API.editPDV(body.pdv_id, {
				pdv_name: body.pdv_name,
				pdv_type: body.pdv_type, 
				venue_id: body.venue_id, 
				all_events: body.all_events,
				location: body.location, 
				terminal_id: pdv.terminal_id,
				external_terminal: pdv.external_terminal,
				report_access: pdv.report_access,
				exclude_commission: pdv.exclude_commission,
				money_supervised: pdv.money_supervised,
			}).then(res=>{
				if(res.error) return setErrorPrompts([res.message]);
				var vn = { ...pdv };
				if(newLocation){
					vn.location = { ...newLocation };
					setPdv(vn);
					setEditLocation(false);
					setNewLocation(null);
				}
				if(props.onEditPdv){
					props.onEditPdv(vn);
				}
				Toast.success('Se ha editando el punto de venta.');
			}).catch(err=>{
				setErrorPrompts(['Hubo un error inesperado editando el punto de venta (LCL-11)']);
			}).finally(()=>{
				setLoading(false);
			});
		}
	}

	const onLocationSelected = (location: Location)=>{
		if(!location) return;
		setEditLocation(false);
		setNewLocation(location);
	}

	var changeLocation = ()=>{
		setEditLocation(true);
		setNewLocation(null);
	}

	var onOptionChange = (option: keyof PDVEditData)=>{
		return (v: boolean)=>{
			var old_value = pdv[option];
			var tx = Toast.show('Guardando cambios', {
				loading: true,
				dismissable: false,
			});
			setPdv(p=>({
				...p,
				[option]: v
			}));
			API.editPDV(pdv.pdv_id, {
				[option]: v
			} as any).then(res=>{
				if(res.error){
					setPdv(p=>({ ...p, [option]: old_value }));
					return Toast.error(res.message);
				}
				Toast.success('Configuración guardada');
			}).catch(err=>{
				Toast.error(t('common.unexpected_code', { code: 'LCL-PDVCR-OPTCH' }));
			}).finally(()=>{
				Toast.dismiss(tx);
			})
		}
	}

	var sendReinitialize = (setLoading: SetLoading)=>{
		setLoading(true);
		API.reinitializePdv(pdv.pdv_id).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			setPdv({
				...pdv,
				token: res.data.token
			});
			Toast.success('Se ha reinicializado el PDV. Se desconectaron los PDV activos con este token.');
			setShownModal(null);
		}).catch(err=>{
			setModalPrompts(['Hubo un error inesperado reinicializando el PDV. (LCL-1)']);
		}).finally(()=>{
			setLoading(false);
		});
	}

	var onVenueChange = (venue: Venue)=>{
		setPdv(p=>{
			let pdv = { ...p };
			pdv.venue_id = venue.venue_id;
			pdv.venue_name = venue.venue_name;
			return pdv;
		});
		return true
	}
	var onRemoveVenue = ()=>{
		setPdv(p=>{
			let pdv = { ...p };
			pdv.venue_id = null;
			pdv.venue_name = null;
			return pdv;
		});
	}

	var onTerminalChange = (terminal: PaymentTerminal)=>{
		setPdv(p=>{
			let pdv = { ...p };
			pdv.terminal_id = terminal.terminal_id;
			pdv.terminal_name = terminal.terminal_name;
			return pdv;
		});
		setShownModal(null);
	}
	var onRemoveTerminal = ()=>{
		setPdv(p=>{
			let pdv = { ...p };
			pdv.terminal_id = null;
			pdv.terminal_name = null;
			return pdv;
		});
	}

	if(!IS_CREATE && Number.isNaN(parseInt(pdv_id))){
		return <NotFound />
	}

	if(!IS_CREATE && !pdv){
		return <Header text="Cargando punto de venta" loading />
	}

	const onChangeField = bindFormChange(pdv, setPdv);

	return <>
		{!!pdv?.deleted ? <DeletedBanner date_deleted={pdv.date_deleted} /> : null}
		{!!pdv && <>
			<Groupper title={IS_CREATE ? 'Crear Punto de Venta' : `Editar Punto de Venta`} style={{ maxWidth: 500, margin: 'auto' }} actions={(
				<Button text="Guardar" color="black" onClick={submit} />
			)}>
				{!IS_CREATE && (
					<Toolbar fitted>
						<Toolbar.Item iconName="qrcode" text="Ver token" onClick={bindClick(setShownModal, Modals.REGISTER_TOKEN)} if={hasAccess(UserAccess.PDV.VIEW_PDV_TOKEN)} />
						<Toolbar.Item iconName="refresh" text="Reinicializar" onClick={bindClick(setShownModal, Modals.RESET_TOKEN)} if={hasAccess(UserAccess.PDV.VIEW_PDV_TOKEN)} />
					</Toolbar>
				)}
				<Input label="Nombre" placeholder="Nombre del punto de venta" value={pdv.pdv_name} onChange={onChangeField('pdv_name')} />
				<Dropdown
					value={pdv.pdv_type}
					label="Tipo"
					onValueChange={onChangeField('pdv_type')}
					items={PDV_TYPES.map(s=>({ text: s.type_name, value: s.pdv_type }))}
				/>
				<Input readonly
					label="Recinto"
					value={pdv.venue_name || ''}
					buttons={(
						<Button style={{ minWidth: 0, paddingRight: 20 }} icon iconStyle={{ margin: 0 }} iconName={pdv.venue_id ? 'remove' : 'pen'} onClick={pdv.venue_id ? onRemoveVenue : bindClick(setShownModal, Modals.VENUE_MODAL)} />
					)}
				/>
				<Field label="Terminal bancaria" amount={pdv.external_terminal ? 1 : 2}>
					<Dropdown
						placeholder="Tipo de terminal"
						value={pdv.external_terminal ? 1 : 0}
						onValueChange={onChangeField('external_terminal')}
						items={[
							{ text: 'Terminal de AREMA', value: 0 },
							{ text: 'Terminal propia', value: 1 }
						]}
					/>
					{!pdv.external_terminal && (
						<Input readonly
							placeholder="Terminal bancaria"
							value={pdv.terminal_name || ''}
							buttons={(
								<Button style={{ minWidth: 0, paddingRight: 20 }} icon iconStyle={{ margin: 0 }} iconName={pdv.terminal_id ? 'remove' : 'pen'} onClick={pdv.terminal_id ? onRemoveTerminal : bindClick(setShownModal, Modals.TERMINAL_MODAL)} />
							)}
						/>
					)}
				</Field>
				{!!pdv.external_terminal && (
					<Message type="info" style={{ marginBottom: 10 }} centered>
						El PDV tiene asignado <b>Terminal Propia</b>, por lo cual le saldrá la opción de cobrar con terminal fuera de control de AREMA. <br />
						Esto requiere que el taquillero confirme el cobro una vez lo haya realizado manualmente.
					</Message>
				)}
				{!editLocation && (newLocation || pdv?.location) ? (
					<LocationView 
						allowEdit={['name', 'street', 'exterior_number', 'interior_number', 'neighborhood', 'city']}
						location={newLocation || pdv?.location} 
						onChange={setNewLocation} 
						onButtonClick={changeLocation} 
					/>
				) : (
					<PlacesSearch
						label="Ubicación"
						options={{
							fields: ["formatted_address", "geometry", "name", "address_components", "place_id"],
							strictBounds: false,
							language: 'es',
							types: ["establishment"],
							componentRestrictions: { country: 'mx' }
						}}
						onLocation={onLocationSelected}
					/>
				)}
				<Message list={errorPrompts} type="error" style={{ marginTop: 15 }} />
			</Groupper>

			{!IS_CREATE && (
				<Table style={{ margin: 'auto', marginTop: 15, maxWidth: 500 }} celled>
					<Table.Head title={'Opciones'} />
					<Table.Body collapsingIndexes={[0]} data={[
						[<Checkbox checked={pdv.all_events} onChange={onOptionChange('all_events')} />, <OptionText title="Acceso a todos los eventos" text="Este PDV tendrá permiso para vender todos los eventos" />],
						...(hasAccess(UserAccess.PDV.DISABLE_COMMISSION) ? [
							[<Checkbox checked={pdv.exclude_commission} onChange={onOptionChange('exclude_commission')} />, <OptionText title="Sin cargo por servicio" text="La venta de boletos en este PDV no tendrán cargo por servicio" />]
						] : []),
						[<Checkbox checked={pdv.report_access} onChange={onOptionChange('report_access')} />, <OptionText title="Acceso a reportes" text="Este PDV tendrá permiso para ver los reportes de aforo de los eventos a los cuales tiene asignados" />],
						...(hasAccess(UserAccess.CONTABILIDAD.EDIT_MONEY_SUPERVISE) ? [
							[<Checkbox checked={pdv.money_supervised} onChange={onOptionChange('money_supervised')} />, <OptionText title="Supervisión de efectivos" text="El PDV tendrá conciliación de efectivos" />],
						] : []),
						[<Checkbox checked={pdv.ignore_limits} onChange={onOptionChange('ignore_limits')} />, <OptionText title="Sin limites de venta" text="Este PDV podrá vender sobre los limites de cantidad de boletos" />],
					]} />
				</Table>
			)}
		</>}
		{!IS_CREATE && hasAccess(UserAccess.PDV.VIEW_PDV_TOKEN) && (
			<Modal open={shownModal === Modals.REGISTER_TOKEN} size='mini' onClose={bindClick(setShownModal)}>
				<Modal.Header>Token de registro</Modal.Header>
				<Modal.Content>
					<div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
						<div style={{ marginBottom: 10, fontWeight: 'bold', maxWidth: 300, textAlign: 'center' }}>
							Este token se usará para registrar el PDV en la plataforma.
						</div>
						<QRCodeSVG value={`${pdv.pdv_id}v${pdv.token}`} width={300} />
						<div className='fr mono' style={{ fontSize: 28, fontWeight: 'bold', marginTop: 10 }}>
							{`${pdv.pdv_id}v${pdv.token}`}
						</div>
						<div style={{ marginTop: -5, color: 'gray' }}>
							Código: {user.user_id}
						</div>
					</div>
				</Modal.Content>
				<Modal.Actions>
					<Button text='Cerrar' basic onClick={bindClick(setShownModal)} />
				</Modal.Actions>
			</Modal>
		)}
		{!IS_CREATE && hasAccess(UserAccess.PDV.RESET_PDV) && (
			<Modal open={shownModal === Modals.RESET_TOKEN} size='tiny' onClose={bindClick(setShownModal)}>
				<Modal.Header>Reinicializar PDV</Modal.Header>
				<Modal.Content>
					<Header text='¿Reinicializar PDV?' style={{ marginBottom: 20 }} />
					<Message type='info' centered>
						Esto desconectará cualquier PDV que se haya registrado con este token. Los PDV desconectados tendrán que registrarse con un token nuevamente.
					</Message>
					<Checkbox checked={!!modalConfirm} onChange={bindClick(setModalConfirm, true)} label={'Deseo reinicializar el PDV'} style={{ marginTop: 20 }} />
					<Message list={modalPrompts} type='error' />
				</Modal.Content>
				<Modal.Actions>
					<Button text='Cerrar' basic onClick={bindClick(setShownModal)} />
					<Button text='Continuar' color='black' disabled={!modalConfirm} onClick={sendReinitialize} />
				</Modal.Actions>
			</Modal>
		)}

		<SearchVenueSectionModal
			open={Modals.VENUE_MODAL === shownModal}
			onClose={bindClick(setShownModal)}
			onVenueSelected={onVenueChange}
		/>

		<SearchTerminalModal
			open={Modals.TERMINAL_MODAL === shownModal}
			onClose={bindClick(setShownModal)}
			onSelected={onTerminalChange}
		/>
	</>
}

export default PDVCreate;